import React, { useMemo, useState } from 'react';
import { Column } from 'react-table';
import { displayOptionalNumericTableValue, formatDateObjShort, numberWithCommas } from '../../../../../../../helpers';
import { TiktokInfluencerPost } from '@round/api';
import PaginationTable from '../../../../../../../ui/PaginationTable/PaginationTable';
import styles from './PreviousPromotions.module.css';
import Skeleton from '../../../../../../../ui/Skeleton/Skeleton';
import { VideoStatsWithData } from '../Promotions';
import { InfluencerPlan } from '../../../../../../../App.types';
import useNonNullContext from '../../../../../../../Hooks/useNonNullContext';
import { OptionsContext } from '../../../../../../../contexts/OptionsContext/OptionsContext';

type PreviousPromotionsTableProps = {
    loading: boolean;
    errorLoading: boolean;
    posts: TiktokInfluencerPost[];
    results: VideoStatsWithData[];
    influencerPlans: InfluencerPlan[];
};

type PreviousPromotionsTableRow = {
    campaignBrand: string;
    campaignRelease: string;
    campaignId: number | null;
    datePosted: number | null;
    cost: string | null;
    videoPlays: number | null;
    likes: number | null;
    comments: number | null;
    shares: number | null;
    saves: number | null;
};

const PreviousPromotions = ({
    posts,
    results,
    loading,
    errorLoading,
    influencerPlans,
}: PreviousPromotionsTableProps) => {
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const { currencies } = useNonNullContext(OptionsContext);

    const rows: PreviousPromotionsTableRow[] = useMemo(
        () =>
            posts
                .filter((p) => !!p.tiktok_post)
                .map((p) => {
                    const result = results.find((result) => result.video_id === p.tiktok_post);
                    const plan = influencerPlans.find((plan) => plan.id === p.plan_id);
                    const postCurrency = currencies.find((currency) => currency.id === p.currency_id);
                    return {
                        campaignBrand: plan?.release.brand.name ?? '-',
                        campaignRelease: plan?.release.name ?? '-',
                        campaignId: plan?.release.id ?? null,
                        datePosted: result?.create_time ?? null,
                        cost:
                            typeof p.cost !== 'undefined'
                                ? `${postCurrency?.symbol || ''}${numberWithCommas(p.cost)}`
                                : '-',
                        videoPlays: result?.play_count ?? null,
                        likes: result?.digg_count ?? null,
                        comments: result?.comment_count ?? null,
                        shares: result?.share_count ?? null,
                        saves: result?.save_count ?? null,
                    };
                }),
        [currencies, influencerPlans, posts, results]
    );

    const columns = useMemo<Column<PreviousPromotionsTableRow>[]>(
        () => [
            {
                Header: 'Campaign Brand',
                accessor: 'campaignBrand',
                Cell: ({ value, row: { original } }) => {
                    if (loading) {
                        return <Skeleton />;
                    }

                    return (
                        <a href={`/campaigns/${original.campaignId}/creators/`} target={'_blank'} rel={'noreferrer'}>
                            {value}
                        </a>
                    );
                },
            },
            {
                Header: 'Campaign Release',
                accessor: 'campaignRelease',
                Cell: ({ value }) => {
                    if (loading) {
                        return <Skeleton />;
                    }

                    return <>{value}</>;
                },
            },
            {
                Header: 'Date Posted',
                accessor: 'datePosted',
                Cell: ({ value }) => {
                    if (loading) {
                        return <Skeleton />;
                    }

                    return <>{typeof value === 'number' ? formatDateObjShort(new Date(value * 1000)) : '-'}</>;
                },
            },
            {
                Header: 'Cost',
                accessor: 'cost',
                Cell: ({ value }) => {
                    if (loading) {
                        return <Skeleton />;
                    }

                    return <>{value}</>;
                },
            },
            {
                Header: 'Video Plays',
                accessor: 'videoPlays',
                Cell: ({ value }) => {
                    if (loading) {
                        return <Skeleton />;
                    }

                    return <>{displayOptionalNumericTableValue(value)}</>;
                },
            },
            {
                Header: 'Likes',
                accessor: 'likes',
                Cell: ({ value }) => {
                    if (loading) {
                        return <Skeleton />;
                    }

                    return <>{displayOptionalNumericTableValue(value)}</>;
                },
            },
            {
                Header: 'Comments',
                accessor: 'comments',
                Cell: ({ value }) => {
                    if (loading) {
                        return <Skeleton />;
                    }

                    return <>{displayOptionalNumericTableValue(value)}</>;
                },
            },
            {
                Header: 'Shares',
                accessor: 'shares',
                Cell: ({ value }) => {
                    if (loading) {
                        return <Skeleton />;
                    }

                    return <>{displayOptionalNumericTableValue(value)}</>;
                },
            },
            {
                Header: 'Saves',
                accessor: 'saves',
                Cell: ({ value }) => {
                    if (loading) {
                        return <Skeleton />;
                    }

                    return <>{displayOptionalNumericTableValue(value)}</>;
                },
            },
        ],
        [loading]
    );
    return (
        <div className={styles.container}>
            <div>
                <p className={styles.heading}>Previous promotions</p>
                <p className={styles.subheading}>all time</p>
            </div>
            <PaginationTable
                tableClassName={styles.table}
                loading={loading}
                manualPagination={false}
                noDataLabel={errorLoading ? "Sorry, we're experiencing technical issues" : 'No previous promotions'}
                data={rows}
                columns={columns}
                count={rows.length}
                page={page}
                setPage={setPage}
                pageSize={pageSize}
                setPageSize={(pageSize) => setPageSize(pageSize ?? 10)}
            />
        </div>
    );
};

export default PreviousPromotions;
